/**
 * `ComposeWizardProvider` — the single mount point for the guided
 * "Ota yhteyttä" wizard (027 US6, D-120 single-experience resolution).
 *
 * Every contact entry (the shell-nav menu item, the Messages FAB, the Home
 * quick-access, the content-surface CTAs) opens the SAME wizard by calling
 * `useComposeWizard().open()` (FR-023). The provider owns the open state and
 * mounts the one `ComposeWizard` dialog; consumers never mount their own.
 *
 * Mounted in `Layout` (the member shell, itself under `PortsProvider` — the
 * wizard reads the `messageCompose` port for its topic step). Tests mount it
 * directly around the surface under test via `renderAppWithPorts`.
 */

import { createContext, type JSX, type ReactNode, useContext, useRef, useState } from 'react';

import { ComposeWizard } from './ComposeWizard.js';

export interface ComposeWizardHandle {
  /** Open the wizard at its channel-choice step. */
  open(returnFocusTo?: HTMLElement | null): void;
}

const ComposeWizardContext = createContext<ComposeWizardHandle | null>(null);

export function ComposeWizardProvider({ children }: { readonly children: ReactNode }): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const openerRef = useRef<HTMLElement | null>(null);
  // No manual memo (react-compiler-uniformity): the provider re-renders only
  // when `isOpen` toggles, so a fresh handle identity is inconsequential and
  // the compiler's build pass owns any memoisation.
  const handle: ComposeWizardHandle = {
    open: (returnFocusTo) => {
      openerRef.current =
        returnFocusTo ??
        (document.activeElement instanceof HTMLElement ? document.activeElement : null);
      setIsOpen(true);
    },
  };
  return (
    <ComposeWizardContext.Provider value={handle}>
      {children}
      {/* Mounted only while open: the wizard resolves the `messageCompose`
          port on mount, and surfaces without that port (any Layout consumer
          that never opens the wizard) must not pay or fail for it. */}
      {isOpen ? (
        <ComposeWizard
          isOpen
          onClose={() => setIsOpen(false)}
          onCloseAutoFocus={(event) => {
            // Menu entries supply their stable trigger because the entry itself unmounts.
            if (openerRef.current?.isConnected) {
              event.preventDefault();
              openerRef.current.focus();
            }
          }}
        />
      ) : null}
    </ComposeWizardContext.Provider>
  );
}

/** Resolve the wizard handle; throws when no provider is mounted above. */
export function useComposeWizard(): ComposeWizardHandle {
  const handle = useContext(ComposeWizardContext);
  if (handle === null) {
    throw new Error(
      'useComposeWizard: no ComposeWizardProvider above this component. Mount the provider in the shell (Layout) or around the surface under test.',
    );
  }
  return handle;
}
